import { useState } from 'react';
import { Barcode } from './Barcode';
import { compIssue, compReceive, itemComponents, type Component } from './prodpay-api';
import { printLabels, type LabelData, type LabelFormat } from './label-print';

const ST: Record<string, [string, string]> = {
  декларация: ['✈️ по декларации', 'gold'],
  склад: ['📦 на складе', 'cy'],
  изделие: ['✅ на изделии', 'grn'],
};

// Панель «Комплектующие · штрихкоды»: приём по декларации → склад → списание на изделие.
export function CompPanel({
  itemId,
  dealNo,
  itemName,
}: {
  itemId: number;
  dealNo?: string;
  itemName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [comps, setComps] = useState<Component[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [fmt, setFmt] = useState<LabelFormat>('a4');
  const [byQty, setByQty] = useState(false);

  const sub = [dealNo, itemName].filter(Boolean).join(' · ') || undefined;

  // Одна деталь → одна этикетка; «по qty» — по этикетке на каждый физический экземпляр.
  function toLabels(list: Component[]): LabelData[] {
    const out: LabelData[] = [];
    for (const c of list) {
      const n = byQty ? Math.max(1, c.qty) : 1;
      for (let i = 0; i < n; i++) {
        out.push({
          code: c.barcode,
          title: c.name,
          sub,
          qtyNote:
            byQty && c.qty > 1 ? `${i + 1} из ${c.qty}` : c.qty > 1 ? `×${c.qty}` : undefined,
        });
      }
    }
    return out;
  }

  function printAll() {
    if (comps)
      printLabels({
        title: `Этикетки · ${itemName ?? 'изделие'}`,
        format: fmt,
        labels: toLabels(comps),
      });
  }
  function printOne(c: Component) {
    printLabels({ title: `Этикетка · ${c.name}`, format: fmt, labels: toLabels([c]) });
  }

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && !comps) setComps((await itemComponents(itemId)).components);
  }
  async function act(p: Promise<unknown>) {
    setBusy(true);
    try {
      await p;
      setComps((await itemComponents(itemId)).components);
    } finally {
      setBusy(false);
    }
  }

  const issued = comps?.filter((c) => c.status === 'изделие').length ?? 0;

  return (
    <div style={{ marginTop: 7 }}>
      <button className="link-btn" onClick={toggle}>
        {open ? '▾' : '▸'} Комплектующие · штрихкоды
        {comps && (
          <span className="muted" style={{ fontWeight: 600 }}>
            {' '}
            ({issued}/{comps.length} на изделии)
          </span>
        )}
      </button>
      {open && comps && (
        <div style={{ marginTop: 8, borderTop: '1px dashed var(--gl)', paddingTop: 6 }}>
          {comps.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                marginBottom: 8,
              }}
            >
              <span className="muted small">Печать этикеток:</span>
              <select
                className="sm"
                value={fmt}
                onChange={(e) => setFmt(e.target.value as LabelFormat)}
                title="Формат печати"
              >
                <option value="a4">лист A4 (сетка)</option>
                <option value="roll">рулон 58×40 мм</option>
              </select>
              <label
                className="muted small"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <input
                  type="checkbox"
                  checked={byQty}
                  onChange={(e) => setByQty(e.target.checked)}
                />
                по qty
              </label>
              <button className="btn ghost sm" onClick={printAll}>
                🖨 Печать всех
              </button>
            </div>
          )}
          {comps.map((c) => {
            const st = ST[c.status] ?? ['', ''];
            return (
              <div
                key={c.id}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}
              >
                <span style={{ flex: 'none', lineHeight: 0 }} title={c.barcode}>
                  <Barcode value={c.barcode} height={26} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="small" style={{ fontWeight: 600 }}>
                    {c.name} <span className="muted">×{c.qty}</span>
                  </div>
                  <div className="mono muted" style={{ fontSize: 10 }}>
                    {c.barcode}
                  </div>
                </div>
                <span className={'chip ' + st[1]}>{st[0]}</span>
                {c.status === 'декларация' ? (
                  <button
                    className="btn ghost sm"
                    disabled={busy}
                    onClick={() => act(compReceive(c.id))}
                  >
                    Принять по декл.
                  </button>
                ) : c.status === 'склад' ? (
                  <button
                    className="btn ghost sm"
                    disabled={busy}
                    onClick={() => act(compIssue(c.id))}
                  >
                    Списать на изделие
                  </button>
                ) : null}
                <button className="icon-btn" title="Печать этикетки" onClick={() => printOne(c)}>
                  🖨
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
